import { createRouter, createWebHistory } from 'vue-router';
import { getVanityUrlMetadata } from '@api/vanity';
import { appBasePath } from '@constants';
import { useAppStore } from '@stores/app';
import { loadScriptDeferred } from '@utils/deferred-scripts';
import { registerIntercom } from '@utils/intercom';
import { pageViewed } from '@utils/page-viewed';
import { isSignUpRoute } from '@utils/routing';
import addTrackers from '@utils/trackers';
import { handleAuthentication } from './auth-guard';
import homeRedirects from './redirects/home-redirects';
import investmentsRedirects from './redirects/investments-redirects';
import vcxRedirects from './redirects/vcx-redirects';
import { routes } from './routes';

const router = createRouter({
	history: createWebHistory(`${appBasePath}`),
	scrollBehavior(to, from, savedPosition) {
		if (savedPosition) {
			return savedPosition;
		} else {
			const el = to.hash ? document.getElementById(to.hash.replace('#', '')) : null;
			if (el) {
				return { el, behavior: 'smooth', top: 70 };
			}

			return { left: 0, top: 0 };
		}
	},
	routes
});

router.beforeEach(async (to, from) => {
	const redirectArray = [homeRedirects, investmentsRedirects, vcxRedirects];
	const foundRedirect = redirectArray.find((entry) => to.path.match(entry.regExPaths) !== null);

	if (foundRedirect) {
		return { name: foundRedirect.routeName, replace: true };
	} else if (!to.matched.length) {
		const slug = to.path.substring(1);
		const isValidSlug = /^[a-zA-Z0-9]{1,100}$/.test(slug);

		if (isValidSlug) {
			try {
				const marketingRedirect = await getVanityUrlMetadata(slug);
				return {
					query: {
						utm_source: marketingRedirect.utmSource,
						utm_medium: marketingRedirect.utmMedium,
						utm_campaign: marketingRedirect.utmCampaign,
						utm_content: marketingRedirect.utmContent
					},
					path: marketingRedirect.redirectUrl,
					replace: true
				};
			} catch {
				return '/404';
			}
		}

		return '/404';
	} else {
		return handleAuthentication(to, from);
	}
});

router.isReady().then(async () => {
	const appStore = useAppStore();

	if (!appStore.isMobileWebview) {
		if (import.meta.env.VITE_ENABLE_CONVERSION_PIXELS === 'true') {
			addTrackers();
		}
	}

	loadScriptDeferred({
		src: `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_PLACES_API_KEY}&libraries=places&callback=initMap`,
		id: 'google-maps-script'
	});
});

router.beforeResolve(async (to) => {
	const appStore = useAppStore();
	await appStore.getCurrentBanner(to.path);
});

router.afterEach(async (to) => {
	pageViewed(to);

	await registerIntercom(isSignUpRoute(to.path) ? 'SIGN_UP' : 'STANDARD');
});

export default router;
